// src/Services.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import './Services.css';
import './ServiceCard.css';
import { useNavigate } from 'react-router-dom';
import translations from './lang';

const Services = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const [countries, setCountries] = useState([]);
  const [sortedCountries, setSortedCountries] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState('');

  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapAddress, setMapAddress] = useState('');

  const [vacationsByUser, setVacationsByUser] = useState({});

  const navigate = useNavigate();
  const language = localStorage.getItem('language') || 'ro';
  const t = translations[language];

  const isLoggedIn = Boolean(localStorage.getItem('access_token'));
  const userId = parseInt(localStorage.getItem('user_id'), 10);

  const categoryDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!isCategoryOpen) return;
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isCategoryOpen]);

  const getCityLabel = (city) => {
    if (!city) return '';

    if (language === 'hu') {
      return city.name_hu || city.name_en || city.name_ro || '';
    }

    if (language === 'ro') {
      return city.name_ro || city.name_en || city.name_hu || '';
    }

    return city.name_en || city.name_hu || city.name_ro || '';
  };

  const getCountryLabel = (country) => {
    if (!country) return '';

    if (language === 'hu') {
      return country.name_hu || country.name_ro || country.name || '';
    }

    if (language === 'ro') {
      return country.name_ro || country.name_hu || country.name || '';
    }

    return country.name || country.name_hu || country.name_ro || '';
  };

  const fetchCountries = () => {
    fetch(`${process.env.REACT_APP_API}/countries/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
      },
    })
      .then((res) => res.json())
      .then((data) => setCountries(Array.isArray(data?.data) ? data.data : []))
      .catch((err) => console.error('Error fetching countries:', err));
  };

  const fetchCities = (countryId = '') => {
    if (!countryId) {
      setCities([]);
      return;
    }

    fetch(`${process.env.REACT_APP_API}/cities/?country=${countryId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const rows = Array.isArray(data?.data) ? data.data : [];

        const options = rows
          .map((city) => ({
            value: city.id,
            label: getCityLabel(city),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setCities(options);
      })
      .catch((err) => console.error('Error fetching cities:', err));
  };

  useEffect(() => {
    fetchAllServices();
    fetchCategories();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountryId) {
      fetchCities(selectedCountryId);
    } else {
      setCities([]);
      setSelectedCityId('');
    }
  }, [language, selectedCountryId]);

  useEffect(() => {
    const all = Array.isArray(countries) ? countries : [];
    const svc = Array.isArray(services) ? services : [];

    if (all.length === 0) {
      setSortedCountries([]);
      return;
    }

    const usedCountryIds = new Set(
      svc
        .map((s) => s?.country)
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .map((v) => String(v))
    );

    const inServices = [];
    const notInServices = [];

    all.forEach((c) => {
      const idStr = String(c?.id);
      if (usedCountryIds.has(idStr)) inServices.push(c);
      else notInServices.push(c);
    });

    const byName = (a, b) => getCountryLabel(a).localeCompare(getCountryLabel(b));
    inServices.sort(byName);
    notInServices.sort(byName);

    setSortedCountries([...inServices, ...notInServices]);
  }, [countries, services, language]);

  useEffect(() => {
    if (!selectedCityId) return;

    const exists = cities.some((c) => String(c.value) === String(selectedCityId));
    if (!exists) {
      setSelectedCityId('');
    }
  }, [cities, selectedCityId]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (!services || services.length === 0) return;

    const uniqueProviderIds = [
      ...new Set(
        services
          .map((s) => s?.user_data?.id)
          .filter(Boolean)
      ),
    ];

    Promise.all(
      uniqueProviderIds.map(async (uid) => {
        try {
          const res = await fetch(`${process.env.REACT_APP_API}/api/vacations-by-user-id/${uid}/`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept-Language': language,
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          const data = await res.json().catch(() => ({ data: [] }));
          return [uid, Array.isArray(data?.data) ? data.data : []];
        } catch {
          return [uid, []];
        }
      })
    ).then((entries) => {
      const map = {};
      entries.forEach(([uid, rows]) => { map[uid] = rows; });
      setVacationsByUser(map);
    });
  }, [services, isLoggedIn, language]);

  const getServiceCategoryId = (service) => {
    if (service.category_id !== undefined && service.category_id !== null) return service.category_id;
    if (service.category !== undefined && service.category !== null) return service.category;
    if (service.categoryId !== undefined && service.categoryId !== null) return service.categoryId;

    if (service.category_name) {
      const match = categories.find((c) => c.name === service.category_name);
      return match ? match.id : null;
    }
    return null;
  };

  const groupedCategories = useMemo(() => {
    const rows = Array.isArray(categories) ? categories : [];
    const order = ['beauty', 'health', 'home', 'build', 'creative', 'business', 'food', 'other'];

    const groups = new Map();

    rows.forEach((c) => {
      const groupKey = (c?.group || 'other').toString().trim() || 'other';
      const groupLabel = (c?.group_label || groupKey).toString().trim() || groupKey;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, { key: groupKey, label: groupLabel, items: [] });
      }
      groups.get(groupKey).items.push(c);
    });

    groups.forEach((g) => {
      g.items.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    });

    const ordered = [];
    order.forEach((k) => { if (groups.has(k)) ordered.push(groups.get(k)); });
    groups.forEach((g) => { if (!order.includes(g.key)) ordered.push(g); });

    return ordered;
  }, [categories]);

  const applyFilter = (source) => {
    let filtered = Array.isArray(source) ? source : [];

    if (selectedCategoryId !== 'all') {
      const selectedCatNum = parseInt(selectedCategoryId, 10);
      filtered = filtered.filter((service) => {
        const sid = getServiceCategoryId(service);
        const serviceCatNum = parseInt(sid, 10);
        return !Number.isNaN(serviceCatNum) && serviceCatNum === selectedCatNum;
      });
    }

    if (selectedCountryId) {
      const selectedCountryNum = parseInt(selectedCountryId, 10);
      filtered = filtered.filter((service) => {
        const serviceCountryNum = parseInt(service.country, 10);
        return !Number.isNaN(serviceCountryNum) && serviceCountryNum === selectedCountryNum;
      });
    }

    if (selectedCityId) {
      filtered = filtered.filter((service) => {
        return String(service.city) === String(selectedCityId);
      });
    }

    setFilteredServices(filtered);
  };

  useEffect(() => {
    applyFilter(services);
  }, [services, selectedCategoryId, selectedCountryId, selectedCityId, categories]);

  const handleCountrySelect = (countryId) => {
    setSelectedCountryId(countryId);
    setSelectedCityId('');
  };

  const handleCitySelect = (cityId) => setSelectedCityId(cityId);

  const fetchAllServices = () => {
    fetch(`${process.env.REACT_APP_API}/all-services/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch services');
        return response.json();
      })
      .then((data) => {
        const filtered = (Array.isArray(data?.data) ? data.data : []).filter((service) => {
          if (!service.client || service.client.length === 0) return true;
          const clientRelation = service.client.find((c) => c.user === userId);
          return !(clientRelation && clientRelation.is_blocked);
        });

        setServices(filtered);
      })
      .catch((error) => console.error('Error fetching services:', error));
  };

  const fetchCategories = () => {
    fetch(`${process.env.REACT_APP_API}/service-categories/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
      },
    })
      .then(async (response) => {
        const text = await response.text();
        let payload = null;

        try {
          payload = JSON.parse(text);
        } catch (e) {
          console.error('❌ service-categories: nem JSON válasz:', text);
          throw new Error('service-categories: invalid JSON response');
        }

        if (!response.ok) {
          console.error('❌ service-categories HTTP hiba:', response.status, payload);
          throw new Error(payload?.error || payload?.message || 'Failed to fetch categories');
        }

        return payload;
      })
      .then((data) => {
        const rows = Array.isArray(data?.data) ? data.data : [];
        setCategories(rows);
      })
      .catch((error) => {
        console.error('❌ Error fetching categories:', error);
        setCategories([]);
      });
  };

  const toggleCategoryDropdown = () => setIsCategoryOpen((v) => !v);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setIsCategoryOpen(false);
  };

  const toggleDetails = (id) => {
    setExpandedServiceId(expandedServiceId === id ? null : id);
  };

  const redirectToBooking = (uId, serviceId) => {
    const logged = Boolean(localStorage.getItem('access_token'));
    if (!logged) {
      navigate('/login');
    } else if (uId && serviceId) {
      navigate(`/booking/${uId}/${serviceId}`);
    } else {
      console.error('User ID or Service ID is undefined');
    }
  };

  const selectedCategoryLabel = useMemo(() => {
    if (selectedCategoryId === 'all') return t.all_categories;
    const found = categories.find((c) => parseInt(c.id, 10) === parseInt(selectedCategoryId, 10));
    return found?.name || t.all_categories;
  }, [selectedCategoryId, categories, t.all_categories]);

  const openMapModal = (addr) => {
    if (!addr) return;
    setMapAddress(addr);
    setMapModalOpen(true);
  };

  const fmtDMY = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const pickRelevantVacation = (service) => {
    const providerId = service?.user_data?.id;
    if (!providerId) return null;
    const rows = vacationsByUser[providerId] || [];
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const now = new Date();

    const relevant = rows.filter((v) => v && (v.service === null || v.service === service.id));
    if (relevant.length === 0) return null;

    const active = relevant.find((v) => {
      const s = new Date(v.start_at);
      const e = new Date(v.end_at);
      return s <= now && now <= e;
    });
    if (active) return active;

    const future = relevant
      .filter((v) => new Date(v.start_at) > now)
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

    return future[0] || null;
  };

  return (
    <div className="services-container">
      <div className="category-filter">
        <div className="category-dropdown" ref={categoryDropdownRef}>
          <button
            className="category-dropdown-button"
            onClick={toggleCategoryDropdown}
            aria-haspopup="listbox"
            aria-expanded={isCategoryOpen}
          >
            {selectedCategoryLabel}
            <span className={`dropdown-caret ${isCategoryOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
          </button>

          {isCategoryOpen && (
            <div className="category-dropdown-menu category-dropdown-menu--cards" role="listbox">
              <div className="category-group-card">
                <button
                  type="button"
                  className={`category-card-item ${selectedCategoryId === 'all' ? 'selected' : ''}`}
                  onClick={() => handleCategorySelect('all')}
                >
                  {t.all_categories}
                </button>
              </div>

              {groupedCategories.map((g) => (
                <div className="category-group-card" key={`grp-${g.key}`}>
                  <div className="category-group-card-header">{g.label}</div>

                  <div className="category-group-card-body">
                    {g.items.map((category) => {
                      const isSel = parseInt(selectedCategoryId, 10) === parseInt(category.id, 10);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          className={`category-card-item ${isSel ? 'selected' : ''}`}
                          onClick={() => handleCategorySelect(category.id)}
                        >
                          {category.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="filters-row">
        <div className="country-filter">
          <label className="filter-label">{t.country || 'Ország'}</label>
          <select
            className="filter-select"
            name="country"
            value={selectedCountryId}
            onChange={(e) => handleCountrySelect(e.target.value)}
          >
            <option value="">{t.choose_country || 'Válasszon országot'}</option>
            {sortedCountries && sortedCountries.map((c) => (
              <option key={c.id} value={c.id}>{getCountryLabel(c)}</option>
            ))}
          </select>
        </div>

        <div className="city-filter">
          <label className="filter-label">{t.city || 'Város'}</label>
          <select
            className="filter-select"
            name="city"
            value={selectedCityId}
            onChange={(e) => handleCitySelect(e.target.value)}
            disabled={!selectedCountryId}
          >
            <option value="">
              {selectedCountryId
                ? (t.select_city || t.choose_city || 'Válasszon várost')
                : (t.choose_country_first || 'Előbb válasszon országot')}
            </option>
            {cities.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="service-card-grid">
        {filteredServices.map((service) => {
          const vacation = pickRelevantVacation(service);
          const showVacation = Boolean(vacation);
          const vacText = showVacation
            ? `${t.on_vacation || 'Szabadság'}: ${fmtDMY(vacation.start_at)} – ${fmtDMY(vacation.end_at)}`
            : '';

          return (
            <div key={service.id} className={`service-card ${expandedServiceId === service.id ? 'expanded' : ''}`}>
              <div
                className="service-card-background"
                style={{
                  backgroundImage: service.service_card_picture
                    ? `url(data:image/jpeg;base64,${service.service_card_picture})`
                    : `url(${process.env.REACT_APP_API}/media/profile_pictures/default.jpg)`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  height: '400px',
                  maxHeight: '400px',
                  maxWidth: '400px',
                  borderRadius: '7px',
                  marginBottom: '7px',
                  position: 'relative',
                  overflow: 'hidden',
                  loading: 'lazy',
                }}
              >
                {showVacation && (
                  <div
                    className="vacation-badge"
                    style={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      zIndex: 2,
                      background: 'rgba(220, 38, 38, 0.92)',
                      color: '#fff',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontWeight: 800,
                      fontSize: '14px',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                      backdropFilter: 'blur(1px)',
                      letterSpacing: '0.2px',
                    }}
                    title={vacText}
                    aria-label={vacText}
                  >
                    {vacText}
                  </div>
                )}

                <div className="service-card-content">
                  <h4>{service.name}</h4>
                  <p>{service.description}</p>
                </div>
              </div>

              {expandedServiceId === service.id && (
                <div className="service-card-details">
                  {service.address ? (
                    <p>
                      {t.address || 'Cím'}:{' '}
                      <button
                        type="button"
                        onClick={() => openMapModal(service.address)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0ea5e9',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit',
                        }}
                        aria-label="Cím megnyitása térképen"
                        title={t.address || 'Cím'}
                      >
                        {service.address}
                      </button>
                    </p>
                  ) : null}

                  <p>{t.sms_notification}: {service.SMS_notification ? t.yes : t.no}</p>
                  <p>{t.email_notification}: {service.Email_notification ? t.yes : t.no}</p>
                  <div className="service-card-user-details">
                    <p><strong>{t.provider}:</strong></p>
                    <p>{t.name}: {service.user_data.first_name || 'N/A'} {service.user_data.last_name || 'N/A'}</p>
                  </div>
                </div>
              )}

              <div className="service-card-button-container">
                {isLoggedIn && (
                  <button onClick={() => toggleDetails(service.id)}>
                    {expandedServiceId === service.id ? t.close : t.details}
                  </button>
                )}
                <button onClick={() => redirectToBooking(service.user_data?.id, service.id)}>
                  {t.book}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {mapModalOpen && (
        <div className="modal" role="dialog" aria-modal="true" onClick={() => setMapModalOpen(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 900, width: '95%', height: '70vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h4 style={{ margin: 0 }}>{t.address || 'Cím'}</h4>
              <button className="close-icon" onClick={() => setMapModalOpen(false)} aria-label="Bezárás">×</button>
            </div>

            <div style={{ margin: '8px 0 12px' }}>{mapAddress}</div>

            <iframe
              title="Google Maps"
              width="100%"
              height="100%"
              src={`https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&hl=${encodeURIComponent(language)}&z=15&output=embed`}
              loading="lazy"
              style={{ border: 0, borderRadius: 8, width: '100%', height: 'calc(100% - 60px)' }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.open_in_maps || 'Megnyitás a Google Térképen'}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;